import * as Sentry from "@sentry/browser";

import { isCssPreloadError } from "./chunk_load_error";
import { getErrorMessage } from "./error_message";

// The bundler's preload helper appends a <link rel="stylesheet"> for every
// stylesheet side-car of a dynamically imported chunk and rejects the import if
// that link fires `error`. It rejects *before* the module factory runs, so a
// stylesheet that fails to fetch stops the chunk's JavaScript from executing at
// all — an entire route dies for a missing style rule, and the retry-on-miss
// loader turns that into a forced reload followed by a crash screen. Losing
// styling is always cheaper than losing the page: cancel the rejection so the
// import proceeds, then re-request the stylesheet once so a transient failure
// repairs itself.

// One attempt per stylesheet per page: a second failure means the asset is
// genuinely unreachable for this client, and retrying on every chunk that
// depends on it would multiply requests that are already failing.
const retriedHrefs = new Set<string>();

// Sentry.init runs from a React effect, after the /web_ui_config fetch it needs
// for the DSN — which can easily land after an early route's stylesheet has
// already failed. captureMessage with no client yet is a silent no-op, and
// cancelling the rejection has removed every other trace of the failure, so
// wait for the client instead of dropping the report on the floor.
const SENTRY_READY_POLL_MS = 500;
const SENTRY_READY_ATTEMPTS = 20;

function whenSentryReady(report: () => void, attempt = 0): void {
  if (Sentry.getClient()) {
    report();
    return;
  }
  if (attempt >= SENTRY_READY_ATTEMPTS) {
    return;
  }
  setTimeout(() => whenSentryReady(report, attempt + 1), SENTRY_READY_POLL_MS);
}

function extractHref(message: string): string | null {
  const match = /unable to preload css for (\S+)/i.exec(message);
  return match ? match[1] : null;
}

// Whether this client fetches linked stylesheets at all. A crawler told to skip
// them to save bandwidth, or an extension blocking the asset origin, fails the
// first request and every retry alike — for a reason no change on this side can
// affect — and does so in far greater numbers than the genuinely unreachable
// assets, which it then hides. The document's own linked stylesheets tell the
// two apart: the entry bundle's and the font providers' links are requested long
// before any route chunk, so a client that finished none of them was never going
// to finish this one either.
//
// An inline <style> is no evidence — it needs no request, so it loads fine on
// exactly the clients this identifies — hence the ownerNode check rather than a
// bare `document.styleSheets.length`.
//
// It is a heuristic, and its bias is known: a client on a bad enough connection
// can fail a route stylesheet while the cross-origin font links are still in
// flight, and reads here as one that loads none. Both signals degrade with the
// same variable, so the clients it misreads are the slowest real ones — which is
// why what it feeds is sampled rather than dropped.
export function clientLoadsStylesheets(): boolean {
  return Array.from(document.styleSheets).some(
    (sheet) => sheet.ownerNode instanceof HTMLLinkElement
  );
}

// Cancelling the rejection removes the only symptom this failure had, so every
// occurrence is kept unsampled: the volume is low, and thinning it would make a
// persistent CDN or extension block look like one that healed on retry.
function reportCssPreloadFailure(
  href: string,
  retried: boolean,
  recovered: boolean
): void {
  whenSentryReady(() =>
    Sentry.captureMessage("Stylesheet preload failed", {
      level: "warning",
      fingerprint: ["css-preload-error"],
      tags: {
        css_preload_error: true,
        // Kept apart from `recovered`, which only means anything once we have
        // actually re-requested the stylesheet: a failure we never retried is
        // no evidence that the asset is dead.
        css_preload_retried: retried,
        css_preload_recovered: recovered,
        online: navigator.onLine,
      },
      extra: { href },
    })
  );
}

function refetchStylesheet(href: string): Promise<boolean> {
  return new Promise((resolve) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    // Deliberately *not* the preload helper's `crossOrigin = ""`. Assets are
    // served from a separate origin, so that mode makes the request depend on
    // the edge returning Access-Control-Allow-Origin; a retry that repeats
    // whatever the first attempt did can only repair a narrower set of causes.
    // Rendering needs no CORS here, and the only thing given up is cssRules
    // access on that one sheet, which its single reader already falls back for.
    // A distinct URL sidesteps the browser's negative cache for the request that
    // just failed; unknown query params don't change which object the CDN serves.
    link.href = `${href}${href.includes("?") ? "&" : "?"}css_retry=1`;
    link.addEventListener("load", () => resolve(true));
    link.addEventListener("error", () => {
      link.remove();
      resolve(false);
    });
    document.head.appendChild(link);
  });
}

// The retry is driven by the failed request's own error event, so issuing it
// straight away re-requests while whatever broke the first attempt is almost
// certainly still true. Pausing first trades a slightly longer unstyled window
// for the much larger share of transient failures that clear on their own.
const RETRY_DELAY_MS = 1000;

async function recoverStylesheet(href: string): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
  reportCssPreloadFailure(href, true, await refetchStylesheet(href));
}

// Must run before the first dynamic import, otherwise the rejection it exists to
// cancel has already torn down the route.
export function installCssPreloadRecovery(): void {
  window.addEventListener("vite:preloadError", (event) => {
    if (!isCssPreloadError(event.payload)) {
      return;
    }

    // Leaving the event uncancelled rethrows it out of the preload helper and
    // aborts the import along with the whole route below it.
    event.preventDefault();

    // Past here the Sentry event is the failure's only remaining symptom, so
    // every branch has to report before it returns — including the ones with
    // nothing to retry.
    const message = getErrorMessage(event.payload);
    const href = extractHref(message);
    if (!href || retriedHrefs.has(href)) {
      reportCssPreloadFailure(href ?? message, false, false);
      return;
    }
    retriedHrefs.add(href);

    void recoverStylesheet(href);
  });
}
